import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { formatPhoneNumber, isValidPhoneNumber } from "react-phone-number-input";
import * as yup from "yup";

import useMyClient from "@hooks/useMyClient";
import {
  ConsultAvailabilityFragment as ConsultAvailabilityType,
  ConsultFragment,
  ConsultRescheduleDocument,
  ConsultRescheduleMutation,
  ConsultRescheduleMutationVariables,
  ContactMethodEnum,
  GetJourneyWithExpertAndConsultsQuery,
  GetJourneyWithPlanQuery
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import { primaryExpertRole } from "@utils/modelUtils";

export type FormData = {
  contactMethod: ContactMethodEnum;
  phoneNumber?: string | null | undefined;
};

type Props = {
  consult: ConsultFragment;
  journey: GetJourneyWithExpertAndConsultsQuery["journey"] | GetJourneyWithPlanQuery["journey"];
  onSuccess?: (consult: ConsultFragment) => void;
};

const useSessionRescheduler = ({ consult, journey, onSuccess }: Props) => {
  const [availabilitySelected, setAvailabilitySelected] = useState<ConsultAvailabilityType | null>(null);
  const [baseErrors, setBaseErrors] = useState<string[]>([]);
  const [duration, setDuration] = useState(consult.durationType);
  const [rescheduledConsult, setRescheduledConsult] = useState<ConsultFragment | null>(null);

  const intl = useIntl();
  const myClient = useMyClient();
  const [rescheduleConsult] = useMutation<ConsultRescheduleMutation, ConsultRescheduleMutationVariables>(
    ConsultRescheduleDocument
  );

  const primaryExpert = useMemo(() => primaryExpertRole(journey.expertRoles)?.expert, [journey.expertRoles]);
  const initialPhoneNumber = useMemo(() => {
    if (myClient) {
      return myClient.consultPhoneNumber ?? myClient.verificationPhoneNumber;
    }
    return journey.owner.consultPhoneNumber ?? journey.owner.verificationPhoneNumber;
  }, [journey.owner.consultPhoneNumber, journey.owner.verificationPhoneNumber, myClient]);

  const defaultValues: FormData = {
    contactMethod: consult.contactMethod ?? ContactMethodEnum.ZOOM,
    phoneNumber: consult.phoneNumber?.number ?? initialPhoneNumber?.number ?? null
  };

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      contactMethod: yup
        .string()
        .oneOf(Object.values(ContactMethodEnum))
        .required(intl.formatMessage({ id: "errors.consult.contactMethod.blank" })),
      phoneNumber: yup
        .string()
        .test(
          "is-valid-phone",
          intl.formatMessage({ id: "errors.consult.phoneNumber.blank" }),
          value => !value || isValidPhoneNumber(value)
        )
        .when("contactMethod", {
          is: ContactMethodEnum.PHONE,
          otherwise: schema => schema.nullable(),
          then: schema => schema.required(intl.formatMessage({ id: "errors.consult.phoneNumber.blank" }))
        })
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });
  const contactMethod = form.watch("contactMethod");
  const phoneNumber = form.watch("phoneNumber");

  const onSubmit = useCallback(async () => {
    if (primaryExpert && availabilitySelected) {
      return rescheduleConsult({
        variables: {
          availability: {
            duration: availabilitySelected.duration,
            expertId: availabilitySelected.expert.id,
            startTime: availabilitySelected.startTime
          },
          contactMethod,
          formattedNumber: phoneNumber ? formatPhoneNumber(phoneNumber) : null,
          id: consult.id,
          number: phoneNumber
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.consultReschedule,
          intl,
          onSuccess: () => {
            if (response.data?.consultReschedule?.consult) {
              setRescheduledConsult(response.data.consultReschedule.consult);
              onSuccess?.(response.data.consultReschedule.consult);
            }
          },
          setBaseErrors,
          setError: form.setError,
          txBase: "errors.consult"
        });
      });
    }
  }, [
    availabilitySelected,
    consult.id,
    contactMethod,
    form,
    intl,
    onSuccess,
    phoneNumber,
    primaryExpert,
    rescheduleConsult
  ]);

  return {
    baseErrors,
    form,
    onSubmit,
    state: {
      availabilitySelected,
      duration,
      rescheduledConsult,
      setAvailabilitySelected,
      setDuration,
      setRescheduledConsult
    }
  };
};

export default useSessionRescheduler;
